import { useCallback, useEffect, useMemo, useState } from "react";
import { Api, ApiError, type AgentDetail, type Issue, type ModelRegistry, type Source } from "../api.js";
import { validate } from "../draft.js";
import { useAutosave, type SaveState } from "../useAutosave.js";
import { LaunchStep } from "./LaunchStep.js";
import { PlanStep } from "./PlanStep.js";
import { Preview } from "./Preview.js";
import { TrainStep } from "./TrainStep.js";
import { UserDataStep } from "./UserDataStep.js";
import { useI18n } from "../i18n.js";
import { Badge } from "./ui.js";

export const STEPS = ["plan", "train", "user-data", "launch"] as const;
export type StepId = (typeof STEPS)[number];
const TITLES: Record<StepId, string> = { plan: "Plan", train: "Train", "user-data": "User data", launch: "Launch" };

export function Editor({ api, initial, models, step, onStep }: { api: Api; initial: AgentDetail; models: ModelRegistry | null; step: StepId; onStep: (s: StepId) => void }) {
  const { t } = useI18n();
  const a = useAutosave({ api, agentId: initial.id, initial });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [trainingBusy, setTrainingBusy] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishIssues, setPublishIssues] = useState<Issue[]>([]);
  const [publishError, setPublishError] = useState<string | null>(null);

  // Local validation shows errors as the person types; server messages cover what only it can check.
  const errors = useMemo(() => {
    const e = validate(a.form);
    for (const i of a.serverIssues) e[i.path] ??= i.message;
    return e;
  }, [a.form, a.serverIssues]);
  const errorCount = Object.keys(errors).length;

  const onSources = useCallback((s: Source[]) => setTrainingBusy(s.some((x) => x.status === "pending" || x.status === "indexing")), []);

  // Models default to the recommended ones when the draft has none yet.
  useEffect(() => {
    if (!models) return;
    const gen = models.generative.find((m) => m.default);
    if (!a.form.model?.generative) {
      a.update((f) => ({
        ...f,
        model: {
          generative: f.model?.generative ?? (gen && { provider: gen.provider, model: gen.model }),
          embedding: f.model?.embedding, // chosen and kept by the platform; never shown
        } as never,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models]);

  const publish = async () => {
    setPublishBusy(true);
    setPublishError(null);
    setPublishIssues([]);
    try {
      await a.saveNow();
      // saveNow may have been queued behind an in-flight save; wait until nothing is pending
      const res = await api.publish(initial.id);
      a.syncRevision(res);
    } catch (e) {
      if (e instanceof ApiError && e.code === "publish_blocked") setPublishIssues(e.issues);
      else setPublishError(e instanceof ApiError ? e.message : "Publishing failed. Try again.");
    } finally {
      setPublishBusy(false);
    }
  };
  const unpublish = async () => {
    setPublishBusy(true);
    try {
      a.syncRevision(await api.unpublish(initial.id));
    } catch (e) {
      setPublishError(e instanceof ApiError ? e.message : "Could not unpublish.");
    } finally {
      setPublishBusy(false);
    }
  };

  const idx = STEPS.indexOf(step);
  const name = (a.form.name ?? "").trim() || "New agent";

  return (
    <div className="editor">
      <header className="editor-head">
        <div>
          <a href="#/" className="back">
            ← All agents
          </a>
          <h1>{name}</h1>
        </div>
        <div className="row">
          <SaveIndicator state={a.state} onRetry={() => void a.saveNow()} errorCount={errorCount} />
          <Badge tone={a.detail.status === "published" ? "ok" : "muted"}>{a.detail.status === "published" ? "Published" : a.detail.status === "unpublished" ? "Unpublished" : "Draft"}</Badge>
          <a className="btn secondary" href={`#/agents/${a.detail.id}/activity`}>
            {t("act.link")}
          </a>
          <button className="btn secondary" onClick={() => setPreviewOpen((v) => !v)} aria-pressed={previewOpen}>
            Preview your agent
          </button>
        </div>
      </header>

      {a.state.kind === "conflict" && (
        <div className="banner warn" role="alert">
          <p>{a.state.message} Your changes on this screen are not lost.</p>
          <div className="row">
            <button className="btn primary" onClick={a.overwrite}>Keep my changes</button>
            <button className="btn secondary" onClick={() => void a.reload()}>Load the other version</button>
          </div>
        </div>
      )}

      <div className={`editor-body ${previewOpen ? "with-preview" : ""}`}>
        <nav className="steps" aria-label="Setup steps">
          <ol>
            {STEPS.map((s, i) => (
              <li key={s}>
                <button className={s === step ? "step-btn active" : "step-btn"} aria-current={s === step ? "step" : undefined} onClick={() => onStep(s)}>
                  <span className="step-num">{i + 1}</span> {TITLES[s]}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <main className="step-main" aria-label={TITLES[step]}>
          <h2>{TITLES[step]}</h2>
          {step === "plan" && <PlanStep form={a.form} update={a.update} errors={errors} models={models} api={api} agentId={initial.id} />}
          {step === "train" && <TrainStep api={api} agentId={initial.id} form={a.form} update={a.update} errors={errors} onSources={onSources} />}
          {step === "user-data" && <UserDataStep form={a.form} update={a.update} errors={errors} api={api} agentId={initial.id} saveNow={a.saveNow} />}
          {step === "launch" && (
            <LaunchStep
              agentId={initial.id}
              form={a.form}
              update={a.update}
              errors={errors}
              detail={a.detail}
              saving={a.state.kind === "saving" || a.state.kind === "pending"}
              trainingBusy={trainingBusy}
              onPublish={publish}
              onUnpublish={unpublish}
              publishIssues={publishIssues}
              publishError={publishError}
              busy={publishBusy}
              api={api}
              saveNow={a.saveNow}
              onDomainsChanged={a.reload}
              onOpenPreview={() => setPreviewOpen(true)}
              onGo={(s) => onStep(s)}
            />
          )}
          <div className="step-nav">
            <button className="btn secondary" disabled={idx === 0} onClick={() => onStep(STEPS[idx - 1] as StepId)}>
              Back
            </button>
            {idx < STEPS.length - 1 && (
              <button className="btn primary" onClick={() => onStep(STEPS[idx + 1] as StepId)}>
                Next
              </button>
            )}
          </div>
        </main>

        <Preview api={api} agentId={initial.id} revision={a.detail.revision} settled={a.state.kind === "saved"} open={previewOpen} onClose={() => setPreviewOpen(false)} />
      </div>
    </div>
  );
}

function SaveIndicator({ state, onRetry, errorCount }: { state: SaveState; onRetry: () => void; errorCount: number }) {
  let text = "";
  let tone = "";
  if (state.kind === "saved") text = errorCount ? `Saved. ${errorCount} field${errorCount === 1 ? "" : "s"} need${errorCount === 1 ? "s" : ""} attention and ${errorCount === 1 ? "is" : "are"} not saved yet.` : "All changes saved";
  else if (state.kind === "saving") text = "Saving…";
  else if (state.kind === "pending") text = "Unsaved changes…";
  else if (state.kind === "error") {
    text = state.message;
    tone = "bad";
  } else text = "Not saved: changed elsewhere";
  return (
    <span className={`save-state ${tone}`} role="status" aria-live="polite">
      {text}
      {state.kind === "error" && state.retryable && (
        <button className="btn ghost" onClick={onRetry}>
          Retry now
        </button>
      )}
    </span>
  );
}
